import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';

const DEGREE_COLORS = {
  1: '#22c55e', // green
  2: '#f59e0b', // amber
  3: '#3b82f6', // blue
  job: '#f97316', // orange
};

function getNodeRadius(node) {
  // Keep candidates small; make job hub noticeably larger.
  if (node?.type === 'job') return 18;
  if (node?.type === 'degree_hub') return 11;
  const d = Number(node?.degree || 3);
  if (d === 1) return 7.2;
  if (d === 2) return 6.2;
  return 5.4;
}

function toDisplayText(value, fallback = '—') {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') {
    const s = value.trim();
    return s || fallback;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const joined = value
      .map((v) => toDisplayText(v, ''))
      .filter(Boolean)
      .join(', ')
      .trim();
    return joined || fallback;
  }
  if (typeof value === 'object') {
    const city = typeof value.city === 'string' ? value.city.trim() : '';
    const country = typeof value.country === 'string' ? value.country.trim() : '';
    const combined = [city, country].filter(Boolean).join(', ');
    if (combined) return combined;
    try {
      return JSON.stringify(value);
    } catch (_) {
      return fallback;
    }
  }
  return fallback;
}

const PARTICIPANT_TIERS = ['Tier 1', 'Tier 2', 'Tier 3'];
const PARTICIPANT_TYPES = [
  'Trading House',
  'Broker / Intermediary',
  'Bank',
  'Exchange / Venue',
  'Refiner / Processor',
  'Corporate / Other',
  'Unknown',
];

function buildParticipantRow(row = {}) {
  const rowId =
    String(row._rowId || '').trim() ||
    `participant_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const tier = PARTICIPANT_TIERS.includes(row.tier) ? row.tier : 'Tier 3';
  const companyType = PARTICIPANT_TYPES.includes(row.company_type) ? row.company_type : 'Unknown';
  const talentCountRaw = Number(row.talent_count);
  const talentCount = Number.isFinite(talentCountRaw) && talentCountRaw > 0 ? Math.round(talentCountRaw) : 0;
  return {
    _rowId: rowId,
    tier,
    company: (row.company || '').trim(),
    company_type: companyType,
    talent_count: talentCount,
    talent_source_note: (row.talent_source_note || '').trim(),
  };
}

function sanitizeParticipantsForSave(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => buildParticipantRow(row))
    .filter((row) => row.company)
    .slice(0, 40);
}

export default function TalentPoolInsights() {
  const { currentUser } = useAuth();
  const [anchorMode, setAnchorMode] = useState('job'); // 'job' | 'market'
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState('');
  const [marketSpecs, setMarketSpecs] = useState([]);
  const [marketSpecId, setMarketSpecId] = useState('');
  const [viewMode, setViewMode] = useState('hub'); // 'hub' | 'split'
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingSpecs, setLoadingSpecs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [preparedFor, setPreparedFor] = useState('');
  const [reportCap, setReportCap] = useState(25); // 25 | 50 | 100
  const [tagMode, setTagMode] = useState('strict'); // strict | broad
  const [mieMode, setMieMode] = useState('intelligence');
  const [mieAudience, setMieAudience] = useState('head_of_trading');
  const [mieTone, setMieTone] = useState('commercial');
  const [mieLayout, setMieLayout] = useState('landscape');
  const [mieAnonymize, setMieAnonymize] = useState(true);
  const [mieMaxCandidates, setMieMaxCandidates] = useState(8);
  const [mieDraftLoading, setMieDraftLoading] = useState(false);
  const [mieAIDraftLoading, setMieAIDraftLoading] = useState(false);
  const [mieSaveLoading, setMieSaveLoading] = useState(false);
  const [mieRenderLoading, setMieRenderLoading] = useState(false);
  const [mieError, setMieError] = useState(null);
  const [mieReportId, setMieReportId] = useState('');
  const [miePayload, setMiePayload] = useState(null);
  const [mieSummary, setMieSummary] = useState('');
  const [mieInsightsText, setMieInsightsText] = useState('');
  const [mieRisksText, setMieRisksText] = useState('');
  const [mieMarketOverviewText, setMieMarketOverviewText] = useState('');
  const [mieMarketConstraintsText, setMieMarketConstraintsText] = useState('');
  const [mieHiringImplicationText, setMieHiringImplicationText] = useState('');
  const [mieMarketStructureJson, setMieMarketStructureJson] = useState('[]');
  const [miePriorityTargetsJson, setMiePriorityTargetsJson] = useState('[]');
  const [mieStrategyTableJson, setMieStrategyTableJson] = useState('[]');
  const [mieActivationPlanJson, setMieActivationPlanJson] = useState('[]');
  const [mieParticipantsRows, setMieParticipantsRows] = useState([]);

  // Curated report inclusion
  const [manualCandidatesById, setManualCandidatesById] = useState({});
  const [excludedIds, setExcludedIds] = useState(() => new Set());
  const [evaluatedCandidates, setEvaluatedCandidates] = useState([]);
  const [evaluatedLoading, setEvaluatedLoading] = useState(false);
  const [evaluatedError, setEvaluatedError] = useState(null);
  const [selectedEvaluatedIds, setSelectedEvaluatedIds] = useState(() => new Set());

  // Add-from-database modal
  const [dbModalOpen, setDbModalOpen] = useState(false);
  const [dbQuery, setDbQuery] = useState('');
  const [dbTitleQuery, setDbTitleQuery] = useState('');
  const [dbSkillTags, setDbSkillTags] = useState('');
  const [dbIndustryTags, setDbIndustryTags] = useState('');
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState(null);
  const [dbResults, setDbResults] = useState([]);
  const [dbSelected, setDbSelected] = useState(() => new Set());
  const [dbNextCursor, setDbNextCursor] = useState(null);
  const [dbHasMore, setDbHasMore] = useState(false);
  const [dbLoadingMore, setDbLoadingMore] = useState(false);

  const fgRef = useRef(null);
  const engineStopFitRef = useRef(false);
  const resizeTimerRef = useRef(null);
  const graphWrapRef = useRef(null);
  const [graphSize, setGraphSize] = useState({ width: 920, height: 620 });

  const measureGraph = useCallback(() => {
    const el = graphWrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = Math.max(260, Math.floor(r.width));
    // Cap height so the grid column doesn’t stretch the canvas to extreme aspect ratios (looks like the graph “falls” in a tall box).
    const hRaw = Math.floor(r.height);
    const h = Math.max(320, Math.min(720, hRaw));
    setGraphSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
  }, []);

  useEffect(() => {
    measureGraph();
    const el = graphWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (resizeTimerRef.current) window.clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = window.setTimeout(() => {
        resizeTimerRef.current = null;
        measureGraph();
      }, 120);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (resizeTimerRef.current) window.clearTimeout(resizeTimerRef.current);
    };
  }, [measureGraph]);

  const getAuthToken = useCallback(async () => {
    if (currentUser && typeof currentUser.getIdToken === 'function') {
      return currentUser.getIdToken();
    }
    return (
      localStorage.getItem('token') ||
      localStorage.getItem('firebaseToken') ||
      localStorage.getItem('idToken') ||
      ''
    );
  }, [currentUser]);

  const fetchJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/job_descriptions?limit=50`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      const list = data.job_descriptions || [];
      setJobs(list);
      // Select a default job for convenience, but do NOT auto-run processing.
      if (!jobId && list.length) setJobId(list[0].id);
    } finally {
      setLoadingJobs(false);
    }
  }, [getAuthToken, jobId]);

  const fetchMarketSpecs = useCallback(async () => {
    setLoadingSpecs(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/market-specs?global=1&limit=100`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      const list = data.market_specs || [];
      setMarketSpecs(list);
      if (!marketSpecId && list.length) setMarketSpecId(list[0].id);
    } finally {
      setLoadingSpecs(false);
    }
  }, [getAuthToken, marketSpecId]);

  const seedDefaultSpecs = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/market-specs/seed-defaults`, {
        method: 'POST',
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      await fetchMarketSpecs();
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [fetchMarketSpecs, getAuthToken]);

  const fetchInsights = useCallback(async () => {
    const hasAnchor = anchorMode === 'job' ? jobId : marketSpecId;
    if (!hasAnchor) return;
    setLoading(true);
    setError(null);
    setSelectedCandidate(null);
    setReport(null);
    setReportError(null);
    try {
      const token = await getAuthToken();
      const qs = new URLSearchParams({ k: '140', min_score: '50', refresh: '1', debug: '1', tag_mode: tagMode });
      const url =
        anchorMode === 'job'
          ? `${API_BASE_URL}/api/talent-pool-insights/${encodeURIComponent(jobId)}?${qs}`
          : `${API_BASE_URL}/api/talent-pool-insights/market/${encodeURIComponent(marketSpecId)}?${qs}&scope=universal`;
      const res = await fetch(url, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setPayload(data);
      setManualCandidatesById({}); // reset manual additions when refreshing insights
      setExcludedIds(new Set()); // reset exclusions when refreshing insights
    } catch (e) {
      setPayload(null);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [anchorMode, getAuthToken, jobId, marketSpecId, tagMode]);

  const fetchEvaluatedCandidates = useCallback(async () => {
    if (anchorMode !== 'job' || !jobId) {
      setEvaluatedCandidates([]);
      setEvaluatedError(null);
      setSelectedEvaluatedIds(new Set());
      return;
    }
    setEvaluatedLoading(true);
    setEvaluatedError(null);
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${API_BASE_URL}/api/job-evaluations/${encodeURIComponent(jobId)}/candidates?limit=120`,
        { headers: { Authorization: token ? `Bearer ${token}` : '' } }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      setEvaluatedCandidates(data.candidates || []);
      setSelectedEvaluatedIds(new Set());
    } catch (e) {
      setEvaluatedCandidates([]);
      setSelectedEvaluatedIds(new Set());
      setEvaluatedError(e.message || String(e));
    } finally {
      setEvaluatedLoading(false);
    }
  }, [anchorMode, getAuthToken, jobId]);

  const searchDatabaseCandidates = useCallback(async ({ append = false } = {}) => {
    if (append) setDbLoadingMore(true);
    else setDbLoading(true);
    setDbError(null);
    try {
      const token = await getAuthToken();
      const cursor = append ? dbNextCursor : null;
      const res = await fetch(`${API_BASE_URL}/api/candidates/search`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filters: {
            name: dbQuery.trim() || undefined,
            title: dbTitleQuery.trim() || undefined,
            ai_skills: dbSkillTags
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
            ai_industries: dbIndustryTags
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          },
          pagination: { page: append ? 2 : 1, limit: 20, cursor },
          sort_by: 'contactName',
          sort_order: 'asc',
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);

      const rows = data.candidates || [];
      const nextCursor = data?.pagination?.next_cursor || null;
      const hasMore = !!data?.pagination?.has_more;

      if (!append) {
        setDbResults(rows);
      } else {
        setDbResults((prev) => {
          const byId = new Map((prev || []).map((r) => [r.id || r.candidate_id, r]));
          for (const r of rows) {
            byId.set(r.id || r.candidate_id, r);
          }
          return Array.from(byId.values());
        });
      }

      setDbNextCursor(nextCursor);
      setDbHasMore(hasMore);
    } catch (e) {
      if (!append) setDbResults([]);
      setDbError(e.message || String(e));
    } finally {
      if (append) setDbLoadingMore(false);
      else setDbLoading(false);
    }
  }, [dbQuery, dbTitleQuery, dbSkillTags, dbIndustryTags, dbNextCursor, getAuthToken]);

  const addSelectedFromDb = useCallback(() => {
    const ids = Array.from(dbSelected);
    if (!ids.length) return;
    setManualCandidatesById((prev) => {
      const next = { ...prev };
      const byId = new Map(
        (dbResults || [])
          .map((c) => [c.id || c.candidate_id, c])
          .filter((entry) => !!entry[0])
      );
      ids.forEach((id) => {
        const c = byId.get(id);
        if (!c) return;
        next[id] = {
          candidate_id: id,
          name: c.contactName || c.name || '—',
          title: c.job_title || c.title || '—',
          company: c.company || null,
          location: c.location || null,
          score: c?.rating?.score ?? c?.score ?? null,
          source: 'manual',
        };
      });
      return next;
    });
    setExcludedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    setDbSelected(new Set());
    setDbModalOpen(false);
  }, [dbSelected, dbResults]);

  const poolCandidates = useMemo(() => payload?.candidates || [], [payload]);

  const includedPoolCandidates = useMemo(
    () => poolCandidates.filter((c) => !excludedIds.has(c.candidate_id)),
    [poolCandidates, excludedIds]
  );

  const manualCandidateIds = useMemo(
    () => new Set(Object.keys(manualCandidatesById || {})),
    [manualCandidatesById]
  );

  const selectedCandidateIdsForReport = useMemo(() => {
    const next = new Set(includedPoolCandidates.map((c) => c.candidate_id).filter(Boolean));
    manualCandidateIds.forEach((id) => next.add(id));
    return next;
  }, [includedPoolCandidates, manualCandidateIds]);

  const curationRows = useMemo(() => {
    const poolById = new Map();
    for (const c of poolCandidates || []) {
      const id = c?.candidate_id;
      if (!id || poolById.has(id)) continue;
      poolById.set(id, {
        ...c,
        candidate_id: id,
        source: 'pool',
      });
    }

    const manualEntries = Object.entries(manualCandidatesById || {});
    for (const [id, c] of manualEntries) {
      if (!id) continue;
      if (poolById.has(id)) {
        poolById.set(id, {
          ...poolById.get(id),
          source: 'pool+manual',
        });
      } else {
        poolById.set(id, {
          ...(c || {}),
          candidate_id: id,
          source: 'manual',
        });
      }
    }

    return Array.from(poolById.values());
  }, [poolCandidates, manualCandidatesById]);

  const generateReport = useCallback(async () => {
    const hasAnchor = anchorMode === 'job' ? jobId : marketSpecId;
    if (!hasAnchor) return;
    setReportLoading(true);
    setReportError(null);
    try {
      const token = await getAuthToken();
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      let graph_png_b64 = '';
      try {
        // react-force-graph may not have painted the current frame yet; retry briefly.
        for (let i = 0; i < 2; i++) {
          const fgCanvas = fgRef.current?.canvas?.();
          if (fgCanvas?.toDataURL) {
            graph_png_b64 = fgCanvas.toDataURL('image/png');
          }
          if (graph_png_b64 && graph_png_b64.startsWith('data:image/png')) break;
          await sleep(80);
        }
      } catch (e) {
        graph_png_b64 = '';
      }
      const selectedCandidateIds = Array.from(selectedCandidateIdsForReport);
      const selectedEvaluatedCandidateIds = Array.from(selectedEvaluatedIds);

      // For job mode we POST so we can attach the visualization image for the PDF.
      const res =
        anchorMode === 'job'
          ? await fetch(`${API_BASE_URL}/api/talent-insights-report/${encodeURIComponent(jobId)}`, {
              method: 'POST',
              headers: {
                Authorization: token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                format: 'pdf',
                top_n: reportCap,
                k: 140,
                min_score: 50,
                graph_png_b64,
                client_name: preparedFor.trim() || null,
                selected_candidate_ids: selectedCandidateIds.length ? selectedCandidateIds : null,
                selected_evaluated_candidate_ids: selectedEvaluatedCandidateIds.length ? selectedEvaluatedCandidateIds : null,
                tag_mode: tagMode,
              }),
            })
          : await fetch(`${API_BASE_URL}/api/talent-insights-report/market/${encodeURIComponent(marketSpecId)}`, {
              method: 'POST',
              headers: {
                Authorization: token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                format: 'pdf',
                top_n: reportCap,
                k: 140,
                min_score: 50,
                scope: 'universal',
                graph_png_b64,
                client_name: preparedFor.trim() || null,
                selected_candidate_ids: selectedCandidateIds.length ? selectedCandidateIds : null,
                tag_mode: tagMode,
              }),
            });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setReport(data);
    } catch (e) {
      setReport(null);
      setReportError(e.message || String(e));
    } finally {
      setReportLoading(false);
    }
  }, [anchorMode, getAuthToken, jobId, marketSpecId, preparedFor, reportCap, selectedCandidateIdsForReport, selectedEvaluatedIds, tagMode]);

  const downloadReportPdf = useCallback(() => {
    if (!report?.pdfB64) return;
    const bytes = Uint8Array.from(atob(report.pdfB64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeTitle = (report?.job?.title || 'talent_insights_report').replace(/[^\w.-]+/g, '_').slice(0, 80);
    a.href = url;
    a.download = `${safeTitle}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [report]);

  const generateMieV2Draft = useCallback(async () => {
    const anchorId = anchorMode === 'job' ? jobId : marketSpecId;
    const anchorType = anchorMode === 'job' ? 'job' : 'market_spec';
    if (!anchorId) return;
    setMieDraftLoading(true);
    setMieError(null);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/mie-v2/reports/draft`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          anchor: { type: anchorType, id: anchorId },
          client_name: preparedFor.trim() || 'Client',
          generation_settings: {
            k: 140,
            min_score: 50,
            tag_mode: tagMode,
            scope: 'universal',
          },
          report_config: {
            mode: mieMode,
            audience_type: mieAudience,
            client_type: 'trading_firm',
            report_tone: mieTone,
            layout: mieLayout,
            theme: 'intelligence_briefing',
            anonymize_candidates: mieAnonymize,
            include_salary_benchmark: false,
            include_competitor_mapping: true,
            include_risk_analysis: true,
            include_engagement_strategy: true,
            include_activation_plan: true,
            max_candidates_in_report: mieMaxCandidates,
            delivery: { format: 'pdf', cta_type: 'request_profiles' },
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setMieReportId(data.report_id || '');
      setMiePayload(data.payload || null);
      setMieSummary(data?.payload?.report_curation?.executive_summary_final || '');
      setMieInsightsText((data?.payload?.report_curation?.key_insights_curated || []).join('\n'));
      setMieRisksText((data?.payload?.report_curation?.risks_curated || []).join('\n'));
      setMieMarketOverviewText(data?.payload?.report_curation?.market_overview_curated || '');
      setMieMarketConstraintsText((data?.payload?.report_curation?.market_constraints_curated || []).join('\n'));
      setMieHiringImplicationText(data?.payload?.report_curation?.hiring_implication_final || '');
      setMieMarketStructureJson(JSON.stringify(data?.payload?.report_curation?.talent_pool_structure_curated || [], null, 2));
      setMiePriorityTargetsJson(JSON.stringify(data?.payload?.report_curation?.priority_targets_curated || [], null, 2));
      setMieStrategyTableJson(JSON.stringify(data?.payload?.report_curation?.engagement_strategy_table_curated || [], null, 2));
      setMieActivationPlanJson(JSON.stringify(data?.payload?.report_curation?.activation_plan_curated || [], null, 2));
      const participantSeed =
        (Array.isArray(data?.payload?.report_curation?.market_participants_curated) &&
          data?.payload?.report_curation?.market_participants_curated?.length
          ? data?.payload?.report_curation?.market_participants_curated
          : data?.payload?.sections?.market_participants) || [];
      setMieParticipantsRows((participantSeed || []).map((row) => buildParticipantRow(row)));
    } catch (e) {
      setMieError(e.message || String(e));
      setMiePayload(null);
    } finally {
      setMieDraftLoading(false);
    }
  }, [
    anchorMode,
    jobId,
    marketSpecId,
    getAuthToken,
    preparedFor,
    tagMode,
    mieMode,
    mieAudience,
    mieTone,
    mieLayout,
    mieAnonymize,
    mieMaxCandidates,
  ]);

  const saveMieV2Curation = useCallback(async () => {
    if (!mieReportId) return;
    setMieSaveLoading(true);
    setMieError(null);
    try {
      const token = await getAuthToken();
      const keyInsights = mieInsightsText
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean)
        .slice(0, 5);
      const risks = mieRisksText
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean)
        .slice(0, 5);
      const marketConstraints = mieMarketConstraintsText
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean)
        .slice(0, 6);
      let marketStructure = [];
      let priorityTargets = [];
      let strategyTable = [];
      let activationPlan = [];
      try {
        marketStructure = JSON.parse(mieMarketStructureJson || '[]');
      } catch (_) {
        throw new Error('Market structure JSON is invalid');
      }
      try {
        priorityTargets = JSON.parse(miePriorityTargetsJson || '[]');
      } catch (_) {
        throw new Error('Priority targets JSON is invalid');
      }
      try {
        strategyTable = JSON.parse(mieStrategyTableJson || '[]');
      } catch (_) {
        throw new Error('Strategy table JSON is invalid');
      }
      try {
        activationPlan = JSON.parse(mieActivationPlanJson || '[]');
      } catch (_) {
        throw new Error('Activation plan JSON is invalid');
      }
      const res = await fetch(`${API_BASE_URL}/api/mie-v2/reports/${encodeURIComponent(mieReportId)}/curation`, {
        method: 'PATCH',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          review_status: 'in_progress',
          executive_summary_final: mieSummary,
          market_overview_curated: mieMarketOverviewText,
          hiring_implication_final: mieHiringImplicationText,
          key_insights_curated: keyInsights,
          market_constraints_curated: marketConstraints,
          risks_curated: risks,
          talent_pool_structure_curated: Array.isArray(marketStructure) ? marketStructure : [],
          priority_targets_curated: Array.isArray(priorityTargets) ? priorityTargets : [],
          engagement_strategy_table_curated: Array.isArray(strategyTable) ? strategyTable : [],
          activation_plan_curated: Array.isArray(activationPlan) ? activationPlan : [],
          market_participants_curated: sanitizeParticipantsForSave(mieParticipantsRows),
          candidate_overrides: curationRows.map((c) => ({
            candidate_id: c.candidate_id,
            include_in_report: selectedCandidateIdsForReport.has(c.candidate_id),
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setMiePayload(data.payload || null);
    } catch (e) {
      setMieError(e.message || String(e));
    } finally {
      setMieSaveLoading(false);
    }
  }, [
    mieReportId,
    getAuthToken,
    mieSummary,
    mieMarketOverviewText,
    mieHiringImplicationText,
    mieInsightsText,
    mieMarketConstraintsText,
    mieRisksText,
    mieMarketStructureJson,
    miePriorityTargetsJson,
    mieStrategyTableJson,
    mieActivationPlanJson,
    mieParticipantsRows,
    curationRows,
    selectedCandidateIdsForReport,
  ]);

  const generateMieV2AIDraft = useCallback(async () => {
    if (!mieReportId) return;
    setMieAIDraftLoading(true);
    setMieError(null);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/mie-v2/reports/${encodeURIComponent(mieReportId)}/draft-content`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      const draft = data.draft || {};
      setMieSummary(draft.executive_summary_final || '');
      setMieInsightsText((draft.key_insights_curated || []).join('\n'));
      setMieRisksText((draft.risks_curated || []).join('\n'));
      setMieMarketOverviewText(draft.market_overview_curated || '');
      setMieMarketConstraintsText((draft.market_constraints_curated || []).join('\n'));
      setMieHiringImplicationText(draft.hiring_implication_final || '');
      setMieMarketStructureJson(JSON.stringify(draft.talent_pool_structure_curated || [], null, 2));
      setMiePriorityTargetsJson(JSON.stringify(draft.priority_targets_curated || [], null, 2));
      setMieStrategyTableJson(JSON.stringify(draft.engagement_strategy_table_curated || [], null, 2));
      setMieActivationPlanJson(JSON.stringify(draft.activation_plan_curated || [], null, 2));
      setMiePayload(data.payload || null);
      const participantSeed =
        (Array.isArray(data?.payload?.report_curation?.market_participants_curated) &&
          data?.payload?.report_curation?.market_participants_curated?.length
          ? data?.payload?.report_curation?.market_participants_curated
          : data?.payload?.sections?.market_participants) || [];
      setMieParticipantsRows((participantSeed || []).map((row) => buildParticipantRow(row)));
    } catch (e) {
      setMieError(e.message || String(e));
    } finally {
      setMieAIDraftLoading(false);
    }
  }, [mieReportId, getAuthToken]);

  const renderMieV2Pdf = useCallback(async () => {
    if (!mieReportId) return;
    setMieRenderLoading(true);
    setMieError(null);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/mie-v2/reports/${encodeURIComponent(mieReportId)}/render`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ format: 'pdf' }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      if (!data.pdfB64) throw new Error('No PDF payload returned');
      const bytes = Uint8Array.from(atob(data.pdfB64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mie_v2_${mieReportId || 'report'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMieError(e.message || String(e));
    } finally {
      setMieRenderLoading(false);
    }
  }, [mieReportId, getAuthToken]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    fetchMarketSpecs();
  }, [fetchMarketSpecs]);

  useEffect(() => {
    fetchEvaluatedCandidates();
  }, [fetchEvaluatedCandidates]);

  // Intentionally do not auto-run on job change. User clicks Refresh/Load.

  const graphData = useMemo(() => {
    const g = payload?.graph;
    if (!g?.nodes?.length) return { nodes: [], links: [] };
    const candidateIdToNodeId = new Map();
    (g.nodes || []).forEach((n) => {
      if (n.type === 'candidate' && n.candidate_id) {
        candidateIdToNodeId.set(n.candidate_id, n.id);
      }
    });
    const hiddenNodeIds = new Set(
      Array.from(excludedIds).map((cid) => candidateIdToNodeId.get(cid)).filter(Boolean)
    );
    if (viewMode === 'hub') {
      // Pin the job hub at the origin so the force sim can’t drift the whole cluster.
      const nodes = g.nodes
        .filter((n) => !(n.type === 'candidate' && hiddenNodeIds.has(n.id)))
        .map((n) => (n.type === 'job' ? { ...n, fx: 0, fy: 0 } : { ...n }));
      const keptNodeIds = new Set(nodes.map((n) => n.id));
      const links = (g.links || [])
        .filter((l) => {
          const sourceId = typeof l.source === 'object' ? l.source?.id : l.source;
          const targetId = typeof l.target === 'object' ? l.target?.id : l.target;
          return keptNodeIds.has(sourceId) && keptNodeIds.has(targetId);
        })
        .map((l) => ({ ...l }));
      return { nodes, links };
    }

    // Grouped-by-degree view:
    // Build 3 fixed “degree hub” nodes and connect candidates to their hub with invisible links.
    // This creates three stable clusters without pinning every candidate to the same coordinate.
    // Spread clusters far enough apart so large groups don’t overlap.
    const DEG_X = { 1: -460, 2: 0, 3: 460 };
    const DEG_Y = -40; // nudge clusters up for better visual centering
    const hubs = [1, 2, 3].map((d) => ({
      id: `deg:${d}`,
      type: 'degree_hub',
      degree: d,
      label: `Degree ${d}`,
      val: 10,
      fx: DEG_X[d],
      fy: DEG_Y,
    }));

    const candidates = g.nodes
      .filter((n) => n.type === 'candidate' && !hiddenNodeIds.has(n.id))
      .map((n) => {
        // Clear any previous fx/fy from other modes.
        const { fx, fy, ...rest } = n;
        return rest;
      });

    const links = candidates.map((c) => ({
      source: `deg:${Number(c.degree || 3)}`,
      target: c.id,
      weight: 1,
      kind: 'degree_cluster',
    }));

    return { nodes: [...hubs, ...candidates], links };
  }, [payload, viewMode, excludedIds]);

  useEffect(() => {
    engineStopFitRef.current = false;
  }, [payload, graphSize.width, graphSize.height, viewMode]);

  const fitGraphToView = useCallback(() => {
    const fg = fgRef.current;
    if (!fg) return;
    try {
      fg.centerAt(0, 0, 0);
      fg.zoomToFit(400, 60);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const onEngineStop = useCallback(() => {
    if (engineStopFitRef.current) return;
    engineStopFitRef.current = true;
    requestAnimationFrame(() => fitGraphToView());
  }, [fitGraphToView]);

  useEffect(() => {
    // Switching view modes should reset pan/zoom so clusters stay centered.
    engineStopFitRef.current = false;
    const t = window.setTimeout(() => fitGraphToView(), 80);
    return () => window.clearTimeout(t);
  }, [viewMode, fitGraphToView]);

  const paintNode = useCallback((node, ctx, globalScale) => {
    const r = getNodeRadius(node);
    const isJob = node.type === 'job';
    const isHub = node.type === 'degree_hub';
    const degree = isJob ? 'job' : node.degree || 3;
    const fill = DEGREE_COLORS[degree] || '#64748b';

    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = fill;
    ctx.fill();

    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 1.2 / globalScale;
    ctx.stroke();

    const label = node.label || '';
    // In split view, label only the 3 clusters to keep it readable.
    if (viewMode === 'split' && node.type === 'candidate') return;

    const fontPx = isJob ? 12 : isHub ? 12 : 10;
    ctx.font = `${fontPx / globalScale}px system-ui, sans-serif`;
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(label.length > 24 ? `${label.slice(0, 22)}…` : label, node.x, node.y + r + 2 / globalScale);
  }, [viewMode]);

  const onNodeClick = useCallback((node) => {
    if (!node || node.type !== 'candidate') {
      setSelectedCandidate(null);
      return;
    }
    setSelectedCandidate({
      candidate_id: node.candidate_id,
      name: node.label,
      title: toDisplayText(node.title),
      company: toDisplayText(node.company, ''),
      location: toDisplayText(node.location, ''),
      score: node.score,
      degree: node.degree,
      profile_url: `/all-candidates?candidate_id=${encodeURIComponent(node.candidate_id)}`,
    });
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex flex-col md:flex-row gap-3 md:items-end">
        <div className="w-full md:w-56">
          <label className="block text-sm text-gray-600 dark:text-gray-300">Anchor</label>
          <select
            className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            value={anchorMode}
            onChange={(e) => {
              setAnchorMode(e.target.value);
              setPayload(null);
              setError(null);
              setReport(null);
              setReportError(null);
              setSelectedCandidate(null);
              setExcludedIds(new Set());
              setEvaluatedCandidates([]);
              setEvaluatedError(null);
              setSelectedEvaluatedIds(new Set());
            }}
          >
            <option value="job">Job</option>
            <option value="market">Market spec</option>
          </select>
        </div>

        {anchorMode === 'job' ? (
          <div className="flex-1">
            <label className="block text-sm text-gray-600 dark:text-gray-300">Select job</label>
            <select
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              disabled={loadingJobs || !jobs.length}
            >
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.title || j.id}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="flex-1">
            <label className="block text-sm text-gray-600 dark:text-gray-300">Select market spec</label>
            <select
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={marketSpecId}
              onChange={(e) => setMarketSpecId(e.target.value)}
              disabled={loadingSpecs || !marketSpecs.length}
            >
              {marketSpecs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.segment || s.id}
                </option>
              ))}
            </select>
            {!loadingSpecs && marketSpecs.length === 0 && (
              <button
                type="button"
                onClick={seedDefaultSpecs}
                className="mt-2 text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Seed default specs (Precious Metals, Commodity Trading, AI Engineer)
              </button>
            )}
          </div>
        )}
        <div className="w-full md:w-56">
          <label className="block text-sm text-gray-600 dark:text-gray-300">View</label>
          <select
            className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
          >
            <option value="hub">Hub & spoke</option>
            <option value="split">Grouped by degree</option>
          </select>
        </div>
        <div className="w-full md:w-40">
          <label className="block text-sm text-gray-600 dark:text-gray-300">Profiles in PDF</label>
          <select
            className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            value={reportCap}
            onChange={(e) => setReportCap(Number(e.target.value))}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="w-full md:w-40">
          <label className="block text-sm text-gray-600 dark:text-gray-300">Tag mode</label>
          <select
            className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            value={tagMode}
            onChange={(e) => setTagMode(e.target.value)}
          >
            <option value="strict">Strict</option>
            <option value="broad">Broad</option>
          </select>
        </div>
        <div className="w-full md:w-72">
          <label className="block text-sm text-gray-600 dark:text-gray-300">
            Prepared for (company){anchorMode === 'market' ? ' *' : ''}
          </label>
          <input
            className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            value={preparedFor}
            onChange={(e) => setPreparedFor(e.target.value)}
            placeholder={anchorMode === 'market' ? 'e.g. CMC Markets' : 'Optional'}
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setDbModalOpen(true);
            setDbError(null);
            setDbResults([]);
            setDbSelected(new Set());
            setDbSkillTags('');
            setDbIndustryTags('');
            setDbNextCursor(null);
            setDbHasMore(false);
            setDbLoadingMore(false);
          }}
          disabled={reportLoading}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          title="Add a candidate from your database into the report shortlist"
        >
          Add from database
        </button>
        <button
          type="button"
          onClick={fetchInsights}
          disabled={!(anchorMode === 'job' ? jobId : marketSpecId) || loading}
          className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Load talent pool'}
        </button>
        <button
          type="button"
          onClick={generateReport}
          disabled={
            !(anchorMode === 'job' ? jobId : marketSpecId) ||
            reportLoading ||
            (anchorMode === 'market' && !preparedFor.trim())
          }
          className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {reportLoading ? 'Generating…' : 'Generate report (PDF)'}
        </button>
      </div>

      <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/40 dark:bg-indigo-900/10 p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">
            ALICE MIE v2 (Setup -> Curation -> Render)
          </div>
          {mieReportId && (
            <div className="text-[11px] text-indigo-700 dark:text-indigo-300">
              Report ID: <strong>{mieReportId}</strong>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Mode</label>
            <select
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={mieMode}
              onChange={(e) => setMieMode(e.target.value)}
            >
              <option value="lite">Lite</option>
              <option value="intelligence">Intelligence</option>
              <option value="execution">Execution</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Audience</label>
            <select
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={mieAudience}
              onChange={(e) => setMieAudience(e.target.value)}
            >
              <option value="head_of_trading">Head of Trading</option>
              <option value="hr_leader">HR Leader</option>
              <option value="ta_leader">TA Leader</option>
              <option value="ceo">CEO</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Tone</label>
            <select
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={mieTone}
              onChange={(e) => setMieTone(e.target.value)}
            >
              <option value="commercial">Commercial</option>
              <option value="analytical">Analytical</option>
              <option value="executive">Executive</option>
              <option value="consultative">Consultative</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Layout</label>
            <select
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={mieLayout}
              onChange={(e) => setMieLayout(e.target.value)}
            >
              <option value="landscape">Landscape</option>
              <option value="portrait">Portrait</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Max candidates</label>
            <input
              type="number"
              min={1}
              max={25}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={mieMaxCandidates}
              onChange={(e) => setMieMaxCandidates(Number(e.target.value || 8))}
            />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={mieAnonymize}
                onChange={(e) => setMieAnonymize(e.target.checked)}
              />
              Anonymize candidates
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="md:col-span-3">
            <label className="block text-xs text-gray-600 dark:text-gray-300">Executive summary (curated)</label>
            <textarea
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={mieSummary}
              onChange={(e) => setMieSummary(e.target.value)}
              placeholder="Add human-curated summary before rendering client output."
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 dark:text-gray-300">Market overview narrative</label>
            <textarea
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={mieMarketOverviewText}
              onChange={(e) => setMieMarketOverviewText(e.target.value)}
              placeholder="Concise market context for Page 2."
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Hiring implication (single block)</label>
            <textarea
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={mieHiringImplicationText}
              onChange={(e) => setMieHiringImplicationText(e.target.value)}
              placeholder="What action should the client take now?"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Curated insights (one per line)</label>
            <textarea
              rows={4}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={mieInsightsText}
              onChange={(e) => setMieInsightsText(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Curated risks (one per line)</label>
            <textarea
              rows={4}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={mieRisksText}
              onChange={(e) => setMieRisksText(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Market constraints (one per line)</label>
            <textarea
              rows={4}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={mieMarketConstraintsText}
              onChange={(e) => setMieMarketConstraintsText(e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 dark:text-gray-300">Market structure rows JSON (Tier table)</label>
            <textarea
              rows={6}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-xs font-mono"
              value={mieMarketStructureJson}
              onChange={(e) => setMieMarketStructureJson(e.target.value)}
              placeholder='[{"tier":"Tier 1","firms":"MTS Global","profile_type":"Sales + Trading","notes":"Strongest commercial profiles"}]'
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Priority target cards JSON (Top 3)</label>
            <textarea
              rows={6}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-xs font-mono"
              value={miePriorityTargetsJson}
              onChange={(e) => setMiePriorityTargetsJson(e.target.value)}
              placeholder='[{"candidate_id":"cand_001","priority_label":"HIGH","why_this_person_matters":"...","recommended_approach":"..."}]'
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Strategy table JSON</label>
            <textarea
              rows={6}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-xs font-mono"
              value={mieStrategyTableJson}
              onChange={(e) => setMieStrategyTableJson(e.target.value)}
              placeholder='[{"candidate_type":"Tier 1 Sales Profiles","strategy":"Direct targeted outreach"}]'
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-300">Activation plan JSON</label>
            <textarea
              rows={6}
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-xs font-mono"
              value={mieActivationPlanJson}
              onChange={(e) => setMieActivationPlanJson(e.target.value)}
              placeholder='[{"phase":1,"label":"Initial engagement","duration":"Week 1","actions":["Engage top 5 targets"]}]'
            />
          </div>
          <div className="md:col-span-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="block text-xs text-gray-600 dark:text-gray-300">
                Market participants by tier (manual curation)
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setMieParticipantsRows((prev) => [
                      ...prev,
                      buildParticipantRow({
                        tier: 'Tier 3',
                        company: '',
                        company_type: 'Unknown',
                        talent_count: 0,
                        talent_source_note: '',
                      }),
                    ])
                  }
                  className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 text-xs"
                >
                  + Add company
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const participantSeed =
                      (Array.isArray(miePayload?.report_curation?.market_participants_curated) &&
                        miePayload?.report_curation?.market_participants_curated?.length
                        ? miePayload?.report_curation?.market_participants_curated
                        : miePayload?.sections?.market_participants) || [];
                    setMieParticipantsRows((participantSeed || []).map((row) => buildParticipantRow(row)));
                  }}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Reset from auto-list
                </button>
              </div>
            </div>
            <div className="mt-2 overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th className="px-2 py-1 text-left">Tier</th>
                    <th className="px-2 py-1 text-left">Company</th>
                    <th className="px-2 py-1 text-left">Type</th>
                    <th className="px-2 py-1 text-left">Talent count</th>
                    <th className="px-2 py-1 text-left">Likely talent source note</th>
                    <th className="px-2 py-1 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {mieParticipantsRows.length === 0 && (
                    <tr>
                      <td className="px-2 py-2 text-gray-500 dark:text-gray-400" colSpan={6}>
                        No participant rows yet. Click "Add company" to curate manually.
                      </td>
                    </tr>
                  )}
                  {mieParticipantsRows.map((row, idx) => (
                    <tr key={row._rowId || `row-${idx}`} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-2 py-1">
                        <select
                          value={row.tier}
                          onChange={(e) =>
                            setMieParticipantsRows((prev) =>
                              prev.map((item, i) => (i === idx ? { ...item, tier: e.target.value } : item))
                            )
                          }
                          className="w-24 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1"
                        >
                          {PARTICIPANT_TIERS.map((tier) => (
                            <option key={tier} value={tier}>
                              {tier}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1">
                        <input
                          value={row.company}
                          onChange={(e) =>
                            setMieParticipantsRows((prev) =>
                              prev.map((item, i) => (i === idx ? { ...item, company: e.target.value } : item))
                            )
                          }
                          className="w-44 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1"
                          placeholder="Company name"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <select
                          value={row.company_type}
                          onChange={(e) =>
                            setMieParticipantsRows((prev) =>
                              prev.map((item, i) => (i === idx ? { ...item, company_type: e.target.value } : item))
                            )
                          }
                          className="w-40 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1"
                        >
                          {PARTICIPANT_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          min={0}
                          value={row.talent_count}
                          onChange={(e) =>
                            setMieParticipantsRows((prev) =>
                              prev.map((item, i) =>
                                i === idx ? { ...item, talent_count: Math.max(0, Number(e.target.value || 0)) } : item
                              )
                            )
                          }
                          className="w-20 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          value={row.talent_source_note}
                          onChange={(e) =>
                            setMieParticipantsRows((prev) =>
                              prev.map((item, i) => (i === idx ? { ...item, talent_source_note: e.target.value } : item))
                            )
                          }
                          className="w-72 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1"
                          placeholder="Why this company matters as a talent source"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <button
                          type="button"
                          onClick={() => setMieParticipantsRows((prev) => prev.filter((_, i) => i !== idx))}
                          className="rounded bg-red-600 hover:bg-red-700 text-white px-2 py-1"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2 text-xs text-gray-600 dark:text-gray-300">
            <div className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Current shortlist feed</div>
            <div>Pool included: {includedPoolCandidates.length}</div>
            <div>Manual added: {manualCandidateIds.size}</div>
            <div>Final shortlist sent to curation: {selectedCandidateIdsForReport.size}</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={generateMieV2Draft}
            disabled={mieDraftLoading || !(anchorMode === 'job' ? jobId : marketSpecId)}
            className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {mieDraftLoading ? 'Generating draft...' : '1) Generate MIE v2 draft'}
          </button>
          <button
            type="button"
            onClick={generateMieV2AIDraft}
            disabled={mieAIDraftLoading || !mieReportId}
            className="rounded-lg bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {mieAIDraftLoading ? 'Drafting content...' : '1b) Generate AI content draft'}
          </button>
          <button
            type="button"
            onClick={saveMieV2Curation}
            disabled={mieSaveLoading || !mieReportId}
            className="rounded-lg bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {mieSaveLoading ? 'Saving...' : '2) Save curation'}
          </button>
          <button
            type="button"
            onClick={renderMieV2Pdf}
            disabled={mieRenderLoading || !mieReportId}
            className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {mieRenderLoading ? 'Rendering...' : '3) Render MIE v2 PDF'}
          </button>
        </div>

        {mieError && <div className="text-xs text-red-600 dark:text-red-400">{mieError}</div>}
        {miePayload && (
          <div className="text-xs text-gray-600 dark:text-gray-300">
            Draft ready: candidates in payload ={' '}
            <strong>{miePayload?.report_data?.candidate_pool?.total_candidates_found ?? 0}</strong>, final shortlist ={' '}
            <strong>{miePayload?.final_shortlist?.length ?? 0}</strong>.
          </div>
        )}
      </div>

      {dbModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDbModalOpen(false)}
            role="presentation"
          />
          <div className="relative w-full max-w-2xl rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">Add candidates from database</div>
              <button
                type="button"
                onClick={() => setDbModalOpen(false)}
                className="text-xs px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Close
              </button>
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-300">Name contains</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  value={dbQuery}
                  onChange={(e) => setDbQuery(e.target.value)}
                  placeholder="e.g. Darren"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-300">Title contains</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  value={dbTitleQuery}
                  onChange={(e) => setDbTitleQuery(e.target.value)}
                  placeholder="e.g. Trader"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-300">Skill tags (comma-separated)</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  value={dbSkillTags}
                  onChange={(e) => setDbSkillTags(e.target.value)}
                  placeholder="e.g. Precious Metals, Gold"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-300">Industry tags (comma-separated)</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  value={dbIndustryTags}
                  onChange={(e) => setDbIndustryTags(e.target.value)}
                  placeholder="e.g. Precious Metals, Commodity Trading"
                />
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => searchDatabaseCandidates({ append: false })}
                disabled={
                  dbLoading ||
                  (!dbQuery.trim() &&
                    !dbTitleQuery.trim() &&
                    !dbSkillTags.trim() &&
                    !dbIndustryTags.trim())
                }
                className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {dbLoading ? 'Searching…' : 'Search'}
              </button>
              <div className="text-xs text-gray-600 dark:text-gray-300">
                Selected: <strong>{dbSelected.size}</strong> · In report shortlist:{' '}
                <strong>{selectedCandidateIdsForReport.size}</strong> / {reportCap}
              </div>
            </div>

            {dbError && <div className="mt-2 text-xs text-red-600 dark:text-red-400">{dbError}</div>}

            <div className="mt-3 max-h-[46vh] overflow-auto rounded-lg border border-gray-100 dark:border-gray-800">
              {dbResults.length === 0 && !dbLoading ? (
                <div className="p-3 text-xs text-gray-500 dark:text-gray-400">
                  {(!dbQuery.trim() && !dbTitleQuery.trim() && !dbSkillTags.trim() && !dbIndustryTags.trim())
                    ? 'Enter name/title and/or tags, then search.'
                    : 'No results.'}
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 w-10">Add</th>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Title</th>
                      <th className="text-left px-3 py-2">Company</th>
                      <th className="text-left px-3 py-2">Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbResults.map((c) => {
                      const id = c.id || c.candidate_id;
                      const checked = dbSelected.has(id);
                      const already = selectedCandidateIdsForReport.has(id);
                      return (
                        <tr key={id} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!id || already}
                              onChange={(e) => {
                                const v = e.target.checked;
                                setDbSelected((prev) => {
                                  const next = new Set(prev);
                                  if (v) next.add(id);
                                  else next.delete(id);
                                  return next;
                                });
                              }}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-gray-900 dark:text-white">{c.contactName || '—'}</div>
                            {already && <div className="text-[10px] text-gray-500">Already in shortlist</div>}
                          </td>
                          <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{toDisplayText(c.job_title || c.title)}</td>
                          <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{toDisplayText(c.company)}</td>
                          <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{toDisplayText(c.location)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="text-[11px] text-gray-500 dark:text-gray-400">
                {dbResults.length > 0 ? `${dbResults.length} loaded` : ''}
              </div>
              <button
                type="button"
                onClick={() => searchDatabaseCandidates({ append: true })}
                disabled={dbLoading || dbLoadingMore || !dbHasMore}
                className="text-xs px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                {dbLoadingMore ? 'Loading more…' : dbHasMore ? 'Load more' : 'No more results'}
              </button>
            </div>

            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDbSelected(new Set())}
                className="text-xs px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Clear selection
              </button>
              <button
                type="button"
                onClick={addSelectedFromDb}
                disabled={!dbSelected.size}
                className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                Add to report shortlist
              </button>
            </div>
          </div>
        </div>
      )}

      {(reportError || report) && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-sm">
          {reportError ? (
            <div className="text-red-600 dark:text-red-400">{reportError}</div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div className="text-gray-700 dark:text-gray-300">
                  Report preview: <strong>{report?.job?.title || '—'}</strong> · Candidates:{" "}
                  <strong>{report?.summary?.total_candidates ?? 0}</strong>
                  <span className="text-gray-500 dark:text-gray-400"> (initials-only)</span>
                </div>
                <button
                  type="button"
                  onClick={downloadReportPdf}
                  disabled={!report?.pdfB64}
                  className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
                >
                  Download PDF
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-2">
                  <div className="text-gray-500 dark:text-gray-400">Degree 1</div>
                  <div className="text-gray-900 dark:text-white font-semibold">
                    {report?.summary?.degree_distribution?.degree_1 ?? 0}
                  </div>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-2">
                  <div className="text-gray-500 dark:text-gray-400">Degree 2</div>
                  <div className="text-gray-900 dark:text-white font-semibold">
                    {report?.summary?.degree_distribution?.degree_2 ?? 0}
                  </div>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-2">
                  <div className="text-gray-500 dark:text-gray-400">Degree 3</div>
                  <div className="text-gray-900 dark:text-white font-semibold">
                    {report?.summary?.degree_distribution?.degree_3 ?? 0}
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-gray-600 dark:text-gray-300">
                      <th className="text-left py-2 pr-2">Initials</th>
                      <th className="text-left py-2 pr-2">Title</th>
                      <th className="text-left py-2 pr-2">Employer</th>
                      <th className="text-left py-2 pr-2">Location</th>
                      <th className="text-left py-2 pr-2">Score</th>
                      <th className="text-left py-2 pr-2">Degree</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report?.candidates || []).slice(0, 12).map((r, idx) => (
                      <tr key={idx} className="border-t border-gray-100 dark:border-gray-700">
                        <td className="py-1.5 pr-2 font-semibold text-gray-900 dark:text-white">
                          {r.initials || '?'}
                        </td>
                        <td className="py-1.5 pr-2 text-gray-700 dark:text-gray-200">{toDisplayText(r.title)}</td>
                        <td className="py-1.5 pr-2 text-gray-700 dark:text-gray-200">{toDisplayText(r.company)}</td>
                        <td className="py-1.5 pr-2 text-gray-600 dark:text-gray-300">{toDisplayText(r.location)}</td>
                        <td className="py-1.5 pr-2 text-gray-700 dark:text-gray-200">{r.score ?? '—'}</td>
                        <td className="py-1.5 pr-2 text-gray-700 dark:text-gray-200">{r.degree ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                  Showing top {Math.min(12, (report?.candidates || []).length)} rows. Export includes up to 25.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {curationRows.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div className="text-gray-700 dark:text-gray-300">
              Report curation · Included from pool: <strong>{includedPoolCandidates.length}</strong> / {poolCandidates.length}
              {' '}· Added manually: <strong>{manualCandidateIds.size}</strong>
              {' '}· Final shortlist: <strong>{selectedCandidateIdsForReport.size}</strong>
            </div>
            <button
              type="button"
              onClick={() => setExcludedIds(new Set())}
              className="text-xs px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Reset exclusions
            </button>
          </div>

          <div className="mt-3 max-h-64 overflow-auto rounded-lg border border-gray-100 dark:border-gray-800">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 w-14">Use</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Title</th>
                  <th className="text-left px-3 py-2">Company</th>
                  <th className="text-left px-3 py-2">Score</th>
                </tr>
              </thead>
              <tbody>
                {curationRows.slice(0, 160).map((c) => {
                  const id = c.candidate_id;
                  const included = id && selectedCandidateIdsForReport.has(id);
                  return (
                    <tr key={id} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={!!included}
                          disabled={!id}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (!checked) {
                              // Explicitly remove from shortlist regardless of source.
                              setManualCandidatesById((prev) => {
                                if (!id || !prev[id]) return prev;
                                const next = { ...prev };
                                delete next[id];
                                return next;
                              });
                              setExcludedIds((prev) => {
                                const next = new Set(prev);
                                if (id) next.add(id);
                                return next;
                              });
                              return;
                            }

                            // Re-include pool candidate; manual-only rows are re-added from DB modal.
                            setExcludedIds((prev) => {
                              const next = new Set(prev);
                              if (id) next.delete(id);
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-900 dark:text-white">
                        <div>{toDisplayText(c.name)}</div>
                        {c.source === 'manual' && (
                          <div className="text-[10px] text-orange-600 dark:text-orange-400">Added manually</div>
                        )}
                        {c.source === 'pool+manual' && (
                          <div className="text-[10px] text-orange-600 dark:text-orange-400">Pool + manually pinned</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{toDisplayText(c.title)}</td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{toDisplayText(c.company)}</td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{c.score ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
            Uncheck to remove from shortlist immediately. Re-check includes pool candidates again; manual-only candidates can be re-added via “Add from database”.
          </p>
        </div>
      )}

      {anchorMode === 'job' && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div className="text-gray-700 dark:text-gray-300">
              Evaluated candidate samples for report: <strong>{selectedEvaluatedIds.size}</strong>
              {' '}selected{evaluatedCandidates.length ? ` / ${evaluatedCandidates.length} available` : ''}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={fetchEvaluatedCandidates}
                disabled={evaluatedLoading || !jobId}
                className="text-xs px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                {evaluatedLoading ? 'Loading...' : 'Refresh evaluated list'}
              </button>
              <button
                type="button"
                onClick={() => setSelectedEvaluatedIds(new Set(evaluatedCandidates.map((c) => c.candidate_id).filter(Boolean)))}
                disabled={!evaluatedCandidates.length}
                className="text-xs px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => setSelectedEvaluatedIds(new Set())}
                disabled={!selectedEvaluatedIds.size}
                className="text-xs px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Clear
              </button>
            </div>
          </div>

          {evaluatedError && (
            <div className="mt-2 text-xs text-red-600 dark:text-red-400">{evaluatedError}</div>
          )}

          <div className="mt-3 max-h-64 overflow-auto rounded-lg border border-gray-100 dark:border-gray-800">
            {evaluatedCandidates.length === 0 && !evaluatedLoading ? (
              <div className="p-3 text-xs text-gray-500 dark:text-gray-400">
                No evaluated candidates found for this job yet.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 w-14">Use</th>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Title</th>
                    <th className="text-left px-3 py-2">Score</th>
                    <th className="text-left px-3 py-2">Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {evaluatedCandidates.map((c) => {
                    const id = c.candidate_id;
                    const included = id && selectedEvaluatedIds.has(id);
                    return (
                      <tr key={id || `${c.initials || 'na'}-${c.score || 0}`} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={!!included}
                            disabled={!id}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedEvaluatedIds((prev) => {
                                const next = new Set(prev);
                                if (checked) next.add(id);
                                else next.delete(id);
                                return next;
                              });
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{toDisplayText(c.name, '-')}</td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{toDisplayText(c.title, '-')}</td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{c.score ?? '-'}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                          {c.summary ? `${String(c.summary).slice(0, 120)}${String(c.summary).length > 120 ? '...' : ''}` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
            Selected rows are included as anonymized evaluated samples (initials only) in the PDF report.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <div
            ref={graphWrapRef}
            className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-hidden relative h-[min(72vh,720px)] min-h-[420px]"
          >
            {error && (
              <div className="absolute inset-0 flex items-center justify-center text-red-600 dark:text-red-400 text-sm p-4">
                {error}
              </div>
            )}
            {!error && loading && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-600 dark:text-gray-400 text-sm">
                Loading talent pool…
              </div>
            )}
            {!error && !loading && graphData.nodes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm p-6 text-center">
                No candidates returned for this job. Try again later or lower the minimum score threshold on the backend.
              </div>
            )}
            {!error && graphData.nodes.length > 0 && (
              <ForceGraph2D
                ref={fgRef}
                width={graphSize.width}
                height={graphSize.height}
                graphData={graphData}
                nodeRelSize={1} // radius is controlled in canvas draw
                enableNodeDrag={true}
                nodeCanvasObjectMode={() => 'replace'}
                nodeCanvasObject={paintNode}
                nodePointerArea={(node, ctx) => {
                  const r = getNodeRadius(node);
                  ctx.beginPath();
                  ctx.arc(node.x, node.y, r + 4, 0, 2 * Math.PI);
                }}
                linkWidth={(l) => (viewMode === 'split' ? 0 : Math.max(0.6, (l.weight || 20) / 80))}
                linkColor={(l) => (viewMode === 'split' ? 'rgba(0,0,0,0)' : 'rgba(100,116,139,0.35)')}
                linkDirectionalArrowLength={4}
                linkDirectionalArrowRelPos={1}
                cooldownTicks={120}
                d3VelocityDecay={0.35}
                onEngineStop={onEngineStop}
                onNodeClick={onNodeClick}
                onBackgroundClick={() => setSelectedCandidate(null)}
              />
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-400">
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: DEGREE_COLORS.job }} />
              Job
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: DEGREE_COLORS[1] }} />
              Degree 1
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: DEGREE_COLORS[2] }} />
              Degree 2
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: DEGREE_COLORS[3] }} />
              Degree 3
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">Candidate snapshot</h3>
          {!selectedCandidate ? (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Click a candidate node to see details and jump to their profile.
            </p>
          ) : (
            <div className="mt-3 space-y-2 text-sm">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{selectedCandidate.name}</p>
                <p className="text-gray-600 dark:text-gray-300">
                  {toDisplayText(selectedCandidate.title)}
                  {selectedCandidate.company ? ` · ${selectedCandidate.company}` : ''}
                </p>
                {selectedCandidate.location ? (
                  <p className="text-gray-500 dark:text-gray-400">{selectedCandidate.location}</p>
                ) : null}
              </div>
              <div className="flex gap-2">
                <span className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-xs">
                  Score: <span className="font-semibold">{selectedCandidate.score}</span>
                </span>
                <span className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-xs">
                  Degree: <span className="font-semibold">{selectedCandidate.degree}</span>
                </span>
              </div>
              <a
                href={selectedCandidate.profile_url}
                className="inline-flex items-center justify-center w-full rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-medium"
              >
                View full profile
              </a>
              <button
                type="button"
                onClick={() => {
                  const cid = selectedCandidate.candidate_id;
                  if (!cid) return;
                  setExcludedIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(cid)) next.delete(cid);
                    else next.add(cid);
                    return next;
                  });
                }}
                className="inline-flex items-center justify-center w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {excludedIds.has(selectedCandidate.candidate_id) ? 'Include in report' : 'Exclude from report'}
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                (Opens All Candidates and deep-links to the profile modal.)
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

